import fileDownload from 'react-file-download';

import { request } from './common';

export type JetstarBspImportCounters = {
  totalRows: number;
  totalPnrs: number;
  successPnrs: number;
  failedPnrs: number;
};

export type JetstarBspImportStatus = {
  importId: string;
  status: 'pending' | 'processing' | 'completed' | 'completed_with_errors' | 'failed';
  counters: JetstarBspImportCounters;
  counts?: {
    totalRows: number;
    totalPnrs: number;
    completedPnrs: number;
    successPnrs: number;
    failedPnrs: number;
    skippedRows: number;
  };
  masterJobId?: string | null;
  createdAt?: string;
  updatedAt?: string;
};

export type JetstarBspImportResultItem = {
  pnr: string;
  status: 'pending' | 'processing' | 'success' | 'failed';
  reasonCode?: string;
  reasonMessage?: string;
  orderNumber?: string;
  processedAt?: string;
};

export type JetstarBspImportResultsResponse = {
  importId: string;
  status: JetstarBspImportStatus['status'];
  counters: JetstarBspImportCounters;
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    hasMore: boolean;
  };
  results: Array<JetstarBspImportResultItem>;
};

export type JetstarBspImportFileType = 'jetstar_bsp' | 'travelfusion';

export type JetstarBspImportProvider = 'jetstar' | 'sabre' | 'travelFusion';

/**
 * UI-facing selection combining the file format (fileType) with the upstream
 * source (provider). Jetstar BSP statements can originate either directly from
 * Jetstar or via Sabre, which the backend disambiguates with the provider query
 * param.
 */
export type JetstarBspImportSource = 'jetstar_direct' | 'jetstar_sabre' | 'travelfusion';

export const JETSTAR_BSP_IMPORT_SOURCE_CONFIG: Record<
  JetstarBspImportSource,
  { fileType: JetstarBspImportFileType; provider: JetstarBspImportProvider }
> = {
  jetstar_direct: { fileType: 'jetstar_bsp', provider: 'jetstar' },
  jetstar_sabre: { fileType: 'jetstar_bsp', provider: 'sabre' },
  travelfusion: { fileType: 'travelfusion', provider: 'travelFusion' },
};

type CreateImportResponse = {
  importId: string;
  status: string;
  statusEndpoint: string;
  resultsEndpoint: string;
  errorsCsvEndpoint: string;
  fileType?: JetstarBspImportFileType;
  provider?: JetstarBspImportProvider;
};

function basePath() {
  return `${window.configs.API_HOST}/api/reporting/travel-operations/jetstar-bsp/imports`;
}

class JetstarBspService {
  static async uploadImport(file: File, source: JetstarBspImportSource): Promise<CreateImportResponse> {
    const formData = new FormData();
    formData.append('file', file);

    const { fileType, provider } = JETSTAR_BSP_IMPORT_SOURCE_CONFIG[source];
    const queryParams = new URLSearchParams({
      fileType,
      provider,
    });

    return request(`${basePath()}?${queryParams.toString()}`, {
      method: 'POST',
      body: formData,
    });
  }

  static async getImportStatus(importId: string): Promise<JetstarBspImportStatus> {
    return request(`${basePath()}/${importId}/status`, { method: 'GET' });
  }

  static async getImportResults(
    importId: string,
    page: number,
    pageSize: number,
  ): Promise<JetstarBspImportResultsResponse> {
    const queryParams = new URLSearchParams({
      page: `${page}`,
      pageSize: `${pageSize}`,
    });

    return request(`${basePath()}/${importId}/results?${queryParams.toString()}`, { method: 'GET' });
  }

  static async downloadImportErrorsCsv(importId: string): Promise<void> {
    const csvData = await request(`${basePath()}/${importId}/errors.csv`, {
      method: 'GET',
      download: true,
    });

    await fileDownload(csvData, `jetstar-bsp-import-${importId}-errors.csv`);
  }
}

export default JetstarBspService;
